import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Typography } from '~/components/common/Typography'
import { DateRangePanel } from '~/components/usage-dashboard/DateRangePanel'

export type MonitoringAlertFilterMode = 'all_alerts' | 'date_time_range'

interface MonitoringAlertsFilterDropdownProps {
  filterMode: MonitoringAlertFilterMode
  pendingStartLocal: string
  pendingEndLocal: string
  onSelectAllAlerts: () => void
  onApplyDateTimeRange: (startLocal: string, endLocal: string) => void
  isCompact?: boolean
}

type DropdownView = 'menu' | 'date_time_range'

const DEFAULT_START_TIME = '00:00:00'
const DEFAULT_END_TIME = '23:59:59'
const TIME_WITH_SECONDS_REGEX = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/
const TIME_HOUR_MINUTE_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/

function normalizeTimeWithSeconds(value: string): string | null {
  const trimmed = value.trim()
  if (TIME_WITH_SECONDS_REGEX.test(trimmed)) {
    return trimmed
  }
  if (TIME_HOUR_MINUTE_REGEX.test(trimmed)) {
    return `${trimmed}:00`
  }
  return null
}

function splitLocalDateTime(value: string): { date: string; time: string } {
  if (!value || !value.includes('T')) {
    return { date: '', time: '' }
  }

  const [date, rawTimeValue] = value.split('T')
  const timeWithoutFraction = rawTimeValue.split('.')[0] ?? ''
  const normalizedTime = normalizeTimeWithSeconds(timeWithoutFraction)

  if (!date || !normalizedTime) {
    return { date: '', time: '' }
  }

  return { date, time: normalizedTime }
}

function formatLocalDateTimeForTrigger(value: string): string | null {
  if (!value || !value.includes('T')) {
    return null
  }

  const [datePart, rawTimeValue] = value.split('T')
  const timeWithoutFraction = rawTimeValue.split('.')[0] ?? ''
  const normalizedTime = normalizeTimeWithSeconds(timeWithoutFraction)

  if (!datePart || !normalizedTime) {
    return null
  }

  return `${datePart} ${normalizedTime}`
}

function CaretDownIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox='0 0 16 16' fill='none' className={className}>
      <path
        d='M4 6L8 10L12 6'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function CaretRightIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox='0 0 16 16' fill='none' className={className}>
      <path
        d='M6 4L10 8L6 12'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

export function MonitoringAlertsFilterDropdown({
  filterMode,
  pendingStartLocal,
  pendingEndLocal,
  onSelectAllAlerts,
  onApplyDateTimeRange,
  isCompact = false
}: MonitoringAlertsFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [view, setView] = useState<DropdownView>('menu')
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedStartDate, setSelectedStartDate] = useState('')
  const [selectedEndDate, setSelectedEndDate] = useState('')
  const [selectedStartTime, setSelectedStartTime] = useState(DEFAULT_START_TIME)
  const [selectedEndTime, setSelectedEndTime] = useState(DEFAULT_END_TIME)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  const triggerLabel = useMemo(() => {
    if (filterMode === 'all_alerts') {
      return 'All Alerts'
    }

    const startLabel = formatLocalDateTimeForTrigger(pendingStartLocal)
    const endLabel = formatLocalDateTimeForTrigger(pendingEndLocal)
    if (startLabel && endLabel) {
      return `${startLabel} - ${endLabel}`
    }

    return 'Date & Time Range'
  }, [filterMode, pendingEndLocal, pendingStartLocal])

  const initializeSelectionFromPending = useCallback(() => {
    const startParts = splitLocalDateTime(pendingStartLocal)
    const endParts = splitLocalDateTime(pendingEndLocal)

    setSelectedStartDate(startParts.date)
    setSelectedEndDate(endParts.date)
    setSelectedStartTime(startParts.time || DEFAULT_START_TIME)
    setSelectedEndTime(endParts.time || DEFAULT_END_TIME)
  }, [pendingEndLocal, pendingStartLocal])

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) {
      return
    }

    const rect = triggerRef.current.getBoundingClientRect()
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
      width: rect.width
    })
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen, updatePosition])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setView('menu')
        setErrorMessage('')
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setView('menu')
      setErrorMessage('')
    }
  }, [isOpen])

  const handleToggle = () => setIsOpen(prev => !prev)

  const handleSelectAllAlerts = () => {
    setErrorMessage('')
    onSelectAllAlerts()
    setIsOpen(false)
  }

  const handleOpenDateTimeRange = () => {
    initializeSelectionFromPending()
    setErrorMessage('')
    setView('date_time_range')
  }

  const handleApply = () => {
    if (!selectedStartDate || !selectedEndDate) {
      setErrorMessage('Please select both start and end dates.')
      return
    }

    if (!selectedStartTime || !selectedEndTime) {
      setErrorMessage('Please select both start and end times.')
      return
    }

    const normalizedStartTime = normalizeTimeWithSeconds(selectedStartTime)
    const normalizedEndTime = normalizeTimeWithSeconds(selectedEndTime)

    if (!normalizedStartTime || !normalizedEndTime) {
      setErrorMessage('Use HH:MM:SS in 24-hour format (e.g. 09:30:45).')
      return
    }

    const startLocal = `${selectedStartDate}T${normalizedStartTime}`
    const endLocal = `${selectedEndDate}T${normalizedEndTime}`

    const parsedStart = new Date(startLocal)
    const parsedEnd = new Date(endLocal)

    if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
      setErrorMessage('Please provide valid date/time values.')
      return
    }

    if (parsedStart.getTime() > parsedEnd.getTime()) {
      setErrorMessage('Start must be before or equal to end.')
      return
    }

    setErrorMessage('')
    onApplyDateTimeRange(startLocal, endLocal)
    setIsOpen(false)
  }

  return (
    <>
      <button
        type='button'
        ref={triggerRef}
        onClick={handleToggle}
        className={`inline-flex w-fit self-start items-center rounded-xl border border-[#D6D6D6] ${
          isCompact ? 'h-8 max-w-[280px] gap-2 px-3' : 'h-9 max-w-[453px] gap-3 px-4'
        }`}
        data-testid='monitoring-alert-filter-trigger'
      >
        <Typography
          fontFamily='geist'
          fontSize={isCompact ? 'bodySm' : 'bodyMdMedium'}
          fontWeight='medium'
          className={`overflow-hidden text-ellipsis whitespace-nowrap text-[rgba(0,0,0,0.88)] ${
            isCompact ? 'max-w-[220px]' : 'max-w-[377px]'
          }`}
        >
          {triggerLabel}
        </Typography>
        <CaretDownIcon className='size-4 shrink-0 text-[rgba(0,0,0,0.65)]' />
      </button>

      {isOpen &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width:
                view === 'menu' ? 220 : isCompact ? Math.min(620, window.innerWidth - 32) : 620,
              zIndex: 9999,
              boxShadow:
                '0px 6px 16px rgba(0,0,0,0.08), 0px 3px 6px -4px rgba(0,0,0,0.12), 0px 9px 28px 8px rgba(0,0,0,0.05)'
            }}
            className='overflow-hidden rounded-[16px] bg-white'
            data-testid='monitoring-alert-filter-dropdown'
          >
            {view === 'menu' ? (
              <div className='py-1'>
                <button
                  type='button'
                  className={`flex w-full items-center px-4 text-left hover:bg-tokens-neutral-fill-fill-4 ${isCompact ? 'h-9' : 'h-10'}`}
                  onClick={handleSelectAllAlerts}
                  data-testid='monitoring-alert-filter-option-all'
                >
                  <Typography
                    fontFamily='geist'
                    fontSize={isCompact ? 'bodySm' : 'bodyMd'}
                    fontWeight='medium'
                    className='text-[rgba(0,0,0,0.88)]'
                  >
                    All Alerts
                  </Typography>
                </button>

                <button
                  type='button'
                  className={`flex w-full items-center justify-between px-4 text-left hover:bg-tokens-neutral-fill-fill-4 ${isCompact ? 'h-9' : 'h-10'}`}
                  onClick={handleOpenDateTimeRange}
                  data-testid='monitoring-alert-filter-option-date-time'
                >
                  <Typography
                    fontFamily='geist'
                    fontSize={isCompact ? 'bodySm' : 'bodyMd'}
                    fontWeight='medium'
                    className='text-[rgba(0,0,0,0.88)]'
                  >
                    Date & Time Range
                  </Typography>
                  <CaretRightIcon className='size-4 text-[rgba(0,0,0,0.65)]' />
                </button>
              </div>
            ) : (
              <div>
                <DateRangePanel
                  initialStartDate={selectedStartDate || undefined}
                  initialEndDate={selectedEndDate || undefined}
                  onRangeSelect={(startDate, endDate) => {
                    setSelectedStartDate(startDate)
                    setSelectedEndDate(endDate)
                    setErrorMessage('')
                  }}
                  onBack={() => {
                    setErrorMessage('')
                    setView('menu')
                  }}
                />

                <div className='px-4 pb-4'>
                  <div className='flex items-end gap-3'>
                    <label className='flex flex-1 flex-col gap-1'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        className='text-[rgba(0,0,0,0.65)]'
                      >
                        Start time
                      </Typography>
                      <input
                        type='text'
                        value={selectedStartTime}
                        onChange={event => setSelectedStartTime(event.target.value)}
                        className='h-9 rounded-[10px] border border-[#D6D6D6] px-3 text-[13px]'
                        placeholder='HH:MM:SS'
                        inputMode='numeric'
                        autoComplete='off'
                        spellCheck={false}
                        data-testid='monitoring-alert-filter-start-time-input'
                      />
                    </label>

                    <label className='flex flex-1 flex-col gap-1'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        className='text-[rgba(0,0,0,0.65)]'
                      >
                        End time
                      </Typography>
                      <input
                        type='text'
                        value={selectedEndTime}
                        onChange={event => setSelectedEndTime(event.target.value)}
                        className='h-9 rounded-[10px] border border-[#D6D6D6] px-3 text-[13px]'
                        placeholder='HH:MM:SS'
                        inputMode='numeric'
                        autoComplete='off'
                        spellCheck={false}
                        data-testid='monitoring-alert-filter-end-time-input'
                      />
                    </label>
                  </div>

                  {errorMessage ? (
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      className='mt-3 text-tokens-status-error-text'
                    >
                      {errorMessage}
                    </Typography>
                  ) : null}

                  <div className='mt-4 flex items-center justify-end'>
                    <button
                      type='button'
                      className='h-9 rounded-[10px] bg-[#5B5BD5] px-3 text-[13px] font-medium text-white hover:opacity-95'
                      onClick={handleApply}
                      data-testid='monitoring-alert-filter-apply'
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  )
}

export default MonitoringAlertsFilterDropdown
